import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useResendInvitation, useRevokeInvitation } from '../../mutations';
import type { Invitation } from '../../types';
import { errorMessage } from '@/shared/api/problem';
import { Button } from '@/shared/ui/atoms/Button';
import { Mono } from '@/shared/ui/atoms/Mono';
import { DataTable, type Column } from '@/shared/ui/organisms/DataTable';
import { useToast } from '@/shared/ui/organisms/Toast/useToast';
import { formatDate } from '@/shared/utils/locale';
import '../../../platform/ui/UsersTable/UsersTable.styl';

type Props = {
  invitations: Invitation[];
  tenantNames?: Map<string, string> | undefined;
  canRevoke?: boolean;
  /** The organisation resends its own invitations (a fresh link replaces the previous one). */
  canResend?: boolean;
  /** Extra per-row actions supplied by the page (e.g. resend from the platform console). */
  renderActions?: (invitation: Invitation) => ReactNode;
};

export function InvitationsTable({ invitations, tenantNames, canRevoke = false, canResend = false, renderActions }: Props) {
  const revoke = useRevokeInvitation();
  const resend = useResendInvitation();
  const toast = useToast();
  const { t } = useTranslation();
  const columns: Column<Invitation>[] = [
    { key: 'email', header: t('people.email'), render: (i) => <span className="f-users__name">{i.email}</span> },
    { key: 'displayName', header: t('people.name'), render: (i) => i.displayName ?? '—' },
    ...(tenantNames ? [{ key: 'tenant', header: t('people.tenant'), render: (i: Invitation) => tenantNames.get(i.tenantId) ?? <Mono>{i.tenantId.slice(0, 8)}</Mono> }] : []),
    { key: 'role', header: t('people.role'), render: (i) => t(`roles.${i.role}`) },
    { key: 'expiresAt', header: t('people.expires'), render: (i) => <Mono>{formatDate(i.expiresAt)}</Mono> },
    { key: 'link', header: t('people.link'), render: (i) => (i.inviteUrl ? <code className="u-mono f-users__email">{i.inviteUrl}</code> : <span className="f-users__email">{t('people.sentByEmail')}</span>) },
    ...(renderActions ? [{ key: 'extra', header: '', align: 'right' as const, render: (i: Invitation) => renderActions(i) }] : []),
    ...(canRevoke
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right' as const,
            render: (i: Invitation) => (
              <span className="f-users__actions">
                {canResend ? (
                  <Button variant="ghost" size="sm" disabled={resend.isPending} onClick={() => resend.mutate(i.id, { onSuccess: (next) => toast.show(t('people.resent', { email: next.email }), 'success'), onError: (err) => toast.show(errorMessage(err, t('people.resendError')), 'error') })}>
                    {t('people.resend')}
                  </Button>
                ) : null}
              <Button variant="danger" size="sm" disabled={revoke.isPending} onClick={() => revoke.mutate(i.id, { onSuccess: () => toast.show(t('people.revoked'), 'success'), onError: () => toast.show(t('people.revokeError'), 'error') })}>
                {t('people.revoke')}
              </Button>
              </span>
            )
          }
        ]
      : [])
  ];
  return <DataTable columns={columns} rows={invitations} rowKey={(i) => i.id} empty={t('people.noInvitations')} dense />;
}
